import { Fragment, ReactNode, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { typography } from './Text';
import { useT } from '../../modules/i18n';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  direction: SortDirection;
}

export interface Column<T> {
  key: string;
  header: ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Tailwind width class, e.g. 'w-40'. */
  width?: string;
  render: (row: T) => ReactNode;
  /**
   * Opts this column into client-side sorting — the header becomes a
   * clickable button that cycles asc/desc. Requires `sortValue`; a
   * `sortable` column without one is a no-op (the header stays unclickable)
   * rather than falling back to sorting on `render`'s output, since that can
   * be a `ReactNode`.
   */
  sortable?: boolean;
  /** The comparable value for this row, used only when `sortable` is set. */
  sortValue?: (row: T) => string | number;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyText?: ReactNode;
  className?: string;
  /**
   * Optional extra className for a row's `<tr>` — e.g. a brand-tinted
   * "selected" background. Applied on top of the base row styles, so a
   * caller can highlight a full-width row band without each cell faking
   * its own per-cell highlight. Defaults to no-op; existing callers are
   * unaffected.
   */
  getRowClassName?: (row: T) => string | undefined;
  /**
   * `'fixed'` switches the underlying `<table>` to CSS `table-layout: fixed`
   * — under this mode each `Column.width` truly caps that column, and any
   * column WITHOUT a `width` shares the remaining space equally. Needed to
   * make a `truncate`'d cell (e.g. a long filename) actually ellipsize
   * instead of growing the table (and its `overflow-x-auto` wrapper) wider
   * than the viewport. Defaults to `'auto'` — today's content-driven column
   * sizing — so every existing caller is pixel-for-pixel unaffected.
   */
  layout?: 'auto' | 'fixed';
  /**
   * Renders extra content in its own full-width row immediately below a
   * matching row (see `expandedRowKey`) — e.g. an inline log viewer under
   * the currently selected run. Optional; omitting it (the default) leaves
   * every row exactly as before.
   */
  renderExpanded?: (row: T) => ReactNode;
  /**
   * The `getRowKey` value of the row to expand. When it matches a row AND
   * `renderExpanded` is provided, one extra `<tr><td colSpan={columns.length}>`
   * is rendered directly beneath that row. No row expands when this is
   * unset/doesn't match, or when `renderExpanded` is omitted.
   */
  expandedRowKey?: string;
  /**
   * Initial sort — a sortable column's key plus direction. Purely the
   * starting state (managed internally via `useState`); omit it and the
   * table renders rows in the given order until the user clicks a sortable
   * header.
   */
  defaultSort?: SortState;
}

const alignClass = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

const ariaSortValue: Record<SortDirection, 'ascending' | 'descending'> = {
  asc: 'ascending',
  desc: 'descending',
};

const sortIconByDirection = {
  asc: ChevronUp,
  desc: ChevronDown,
} as const;

/**
 * The Regask data table — a column-def-driven table styled on the foundation:
 * an uppercase muted header row, hairline row dividers, hover highlight, and
 * mono-friendly cells. Backs the catalog list, approvals and any tabular view.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  emptyText,
  className,
  getRowClassName,
  layout = 'auto',
  renderExpanded,
  expandedRowKey,
  defaultSort,
}: DataTableProps<T>) {
  const t = useT();
  const [sort, setSort] = useState<SortState | undefined>(defaultSort);
  const resolvedEmptyText = emptyText ?? t('ui.dataTable.emptyText');

  const sortedRows = useMemo(() => {
    const col = sort && columns.find(c => c.key === sort.key);
    if (!sort || !col?.sortValue) return rows;
    const sortValue = col.sortValue;
    // Decorate with the original index as a tie-breaker — Array#sort is
    // stable in modern engines, but this makes that guarantee explicit.
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const av = sortValue(a.row);
        const bv = sortValue(b.row);
        const cmp =
          typeof av === 'string' && typeof bv === 'string'
            ? av.localeCompare(bv)
            : (av as number) - (bv as number);
        if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
        return a.index - b.index;
      })
      .map(({ row }) => row);
  }, [rows, sort, columns]);

  const toggleSort = (col: Column<T>) => {
    if (!col.sortValue) return;
    setSort(prev =>
      prev?.key === col.key
        ? { key: col.key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, direction: 'asc' },
    );
  };

  return (
    <div className={cx('w-full overflow-x-auto', className)}>
      <table
        className={cx(
          'w-full border-collapse',
          layout === 'fixed' && 'table-fixed',
        )}
      >
        <thead>
          <tr className="border-b border-default bg-muted">
            {columns.map(col => {
              const isActive = sort?.key === col.key;
              let ariaSort: 'ascending' | 'descending' | 'none' | undefined;
              if (col.sortable) {
                ariaSort = isActive ? ariaSortValue[sort!.direction] : 'none';
              }
              const sortIcon = isActive
                ? sortIconByDirection[sort!.direction]
                : ChevronsUpDown;
              return (
                <th
                  key={col.key}
                  aria-sort={ariaSort}
                  className={cx(
                    typography.overline,
                    'px-4 py-2.5 text-fg-tertiary',
                    col.width,
                    alignClass[col.align ?? 'left'],
                  )}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col)}
                      className={cx(
                        'inline-flex items-center gap-1 rounded transition-colors hover:text-fg-secondary',
                        'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgb(var(--c-ring)/0.24)]',
                      )}
                    >
                      {col.header}
                      <Icon
                        as={sortIcon}
                        size="xs"
                        className={isActive ? 'text-fg-secondary' : 'text-fg-muted'}
                      />
                      {isActive && (
                        <span className="sr-only">
                          {t(
                            sort!.direction === 'asc'
                              ? 'ui.dataTable.sortAscending'
                              : 'ui.dataTable.sortDescending',
                          )}
                        </span>
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className={cx(
                  typography.body,
                  'px-4 py-8 text-center text-fg-tertiary',
                )}
              >
                {resolvedEmptyText}
              </td>
            </tr>
          ) : (
            sortedRows.map(row => {
              const key = getRowKey(row);
              // Guard against `undefined === undefined`: an unset
              // `expandedRowKey` must never match a row (even one whose key is
              // undefined), or every such row would expand by default.
              const isExpanded =
                Boolean(renderExpanded) &&
                expandedRowKey !== undefined &&
                key === expandedRowKey;
              return (
                <Fragment key={key}>
                  <tr
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cx(
                      'transition-colors',
                      isExpanded
                        ? 'border-0'
                        : 'border-b border-default last:border-0',
                      onRowClick && 'cursor-pointer hover:bg-muted',
                      getRowClassName?.(row),
                    )}
                  >
                    {columns.map(col => (
                      <td
                        key={col.key}
                        className={cx(
                          typography.body,
                          'px-4 py-3 text-fg align-middle',
                          alignClass[col.align ?? 'left'],
                        )}
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className="border-b border-default last:border-0">
                      <td colSpan={columns.length} className="p-0">
                        {renderExpanded!(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
